import React, { useEffect, useState, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { useCourseStudyPlan } from '../../../../hooks/useCourseStudyPlan'
import { useSessionParams } from '../../../../hooks/useSessionParams'
import { useStepContent } from '../../../../hooks/useStepContent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { useContentSharing, LAST_SEEN_STEP_KEY } from '../../../../context/ContentSharingContext'
import { useTranslation } from 'react-i18next'
import { useContentfulRenderOptions } from '../../../../hooks/useContentfulRenderOptions'
import NavigationHeader from './NavigationHeader'
import { useMentorAI } from '../../../../context/MentorAIContext'
import { HighlightMenu, MenuButton } from 'react-highlight-menu'

interface StudyPlanContentProps {
    onExpand: (expanded: boolean) => void
    isExpanded: boolean
    onSelectText?: (text: string) => void
}

const StudyPlanContent: React.FC<StudyPlanContentProps> = ({ onExpand, isExpanded, onSelectText }) => {
    const { t } = useTranslation(['sidebar', 'mentor'])
    const { _classId: courseId } = useSessionParams()
    const { currentSession, findStepById, error: studyPlanError, isLoading: isStudyPlanLoading } = useCourseStudyPlan(courseId)
    const [localStepId, setLocalStepId] = useState<string>()
    const { stepContent, isLoading: isStepLoading, error: stepError } = useStepContent(localStepId)
    const currentStep = localStepId ? findStepById(localStepId) : undefined
    const { isContentShared, isContentSharer, currentSharedStep, navigateToStep, setCurrentStepId } = useContentSharing()
    const { options } = useContentfulRenderOptions({
        step: stepContent,
        status: currentStep?.status
    })
    const { addTextFragment, canInteract } = useMentorAI()

    // Timer state - using duration from API
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
    const [isTimerExpired, setIsTimerExpired] = useState<boolean>(false)

    // Content ref for the highlight menu
    const contentRef = useRef<HTMLDivElement>(null)

    // Initialize timer when step content changes
    useEffect(() => {
        if (stepContent?.duration !== undefined) {
            // Convert minutes to seconds if duration is not null
            setTimeRemaining(stepContent.duration !== null ? stepContent.duration * 60 : null)
            setIsTimerExpired(false)
        }
    }, [stepContent])

    // Timer countdown effect
    useEffect(() => {
        // Only run timer if timeRemaining is a number (not null)
        if (timeRemaining === null) return;

        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev === null) return null;

                if (prev <= 0) {
                    setIsTimerExpired(true)
                    return prev - 1 // Keep counting in negative
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(timer)
    }, [timeRemaining])

    useEffect(() => {
        if (!currentSession?.steps.length) return;

        // If content is being shared, use the shared step ID directly
        if (isContentShared && currentSharedStep !== null) {
            // Find the step in the current session by its ID
            const sharedStep = currentSession.steps.find(step => step.id === currentSharedStep);

            if (sharedStep) {
                // console.log('Setting current step ID to shared step:', sharedStep.id);
                setLocalStepId(sharedStep.id);
            } else {
                // If the shared step is not in the current session, use the first step
                // console.log('Shared step not found in current session, using first step:', currentSession.steps[0].id);
                setLocalStepId(currentSession.steps[0].id);

                // If I'm the content sharer, update the shared step to the first step
                if (isContentSharer) {
                    // console.log('I am the sharer, updating shared step to first step');
                    navigateToStep(currentSession.steps[0].id);
                }
            }
        } else if (!isContentShared && localStepId === undefined) {
            // If content is not being shared and we don't have a current step, use the first step
            console.log('Setting current step ID to first step:', currentSession.steps[0].id);
            setLocalStepId(currentSession.steps[0].id);
        }
        // If we already have a localStepId and content is not being shared, keep the current step
    }, [currentSession, isContentShared, currentSharedStep, localStepId, isContentSharer, navigateToStep])

    // Update the context whenever localStepId changes
    useEffect(() => {
        if (localStepId) {
            setCurrentStepId(localStepId);
            // Save the step to sessionStorage for future content sharing
            try {
                sessionStorage.setItem(LAST_SEEN_STEP_KEY, localStepId)
            } catch (error) {
                console.error('Error saving last seen step to sessionStorage:', error)
            }
        }
    }, [localStepId, setCurrentStepId]);

    // Handler for Ask Mentor AI action
    const handleAskMentorAI = (selectedText: string) => {
        console.log('📣 addTextFragment called with text:', selectedText);
        
        if (addTextFragment) {
            // Use the addTextFragment function which stores the context without immediate query
            addTextFragment(selectedText);
            console.log('📣 Added fragment to Mentor AI');
        } else {
            console.warn('addTextFragment function is not available');
        }
    };

    const handleStepChange = (stepIndex: number) => {
        if (!currentSession?.steps[stepIndex - 1]) return

        const stepId = currentSession.steps[stepIndex - 1].id;

        // Update local step
        setLocalStepId(stepId)

        // Save the step to sessionStorage for future content sharing
        try {
            sessionStorage.setItem(LAST_SEEN_STEP_KEY, stepId)
        } catch (error) {
            console.error('Error saving last seen step to sessionStorage:', error)
        }

        // If I'm the content sharer, notify others about the navigation
        if (isContentSharer) {
            // Pass the actual step ID instead of the index
            navigateToStep(stepId)
        }

        // Timer will be reset when stepContent changes in the useEffect
        setIsTimerExpired(false)
    }

    const handlePrevStep = (): void => {
        if (!currentStep?.prevStep) return

        // Find the index of the previous step
        const prevStepIndex = currentSession?.steps.findIndex(step => step.id === currentStep.prevStep)
        if (prevStepIndex !== undefined && prevStepIndex >= 0) {
            // Add 1 because our step indices in the UI are 1-based
            handleStepChange(prevStepIndex + 1)
        }
    }

    const handleNextStep = (): void => {
        if (!currentStep?.nextStep) return

        // Find the index of the next step
        const nextStepIndex = currentSession?.steps.findIndex(step => step.id === currentStep.nextStep)
        if (nextStepIndex !== undefined && nextStepIndex >= 0) {
            // Add 1 because our step indices in the UI are 1-based
            handleStepChange(nextStepIndex + 1)
        }
    }

    // Helper function to check if a step ID belongs to the current session
    const isStepInCurrentSession = (stepId: string | undefined): boolean => {
        if (!stepId || !currentSession) return false;
        return currentSession.steps.some(step => step.id === stepId);
    }

    const handleToggleExpand = () => {
        onExpand(!isExpanded);
    }

    if (!currentSession && !isStudyPlanLoading && !studyPlanError) return null

    // Calculate step number and total steps
    const stepNumber = currentSession && localStepId
        ? currentSession.steps.findIndex(step => step.id === localStepId) + 1
        : undefined;
    const totalSteps = currentSession?.steps.length;

    return (
        <Container>
            <NavigationHeader
                onToggleExpand={handleToggleExpand}
                currentSessionTitle={currentSession?.title}
                stepTitle={stepContent?.title || ''}
                stepNumber={stepNumber}
                totalSteps={totalSteps}
                timeRemaining={timeRemaining}
                isTimerExpired={isTimerExpired}
                onPrevStep={handlePrevStep}
                onNextStep={handleNextStep}
                isPrevDisabled={!currentStep?.prevStep || !isStepInCurrentSession(currentStep?.prevStep) || (isContentShared && !isContentSharer)}
                isNextDisabled={!currentStep?.nextStep || !isStepInCurrentSession(currentStep?.nextStep) || (isContentShared && !isContentSharer)}
            />
            <Content ref={contentRef}>
                {isStudyPlanLoading || isStepLoading ? (
                    <LoadingContainer>
                        <FontAwesomeIcon icon={['far', 'spinner']} spin size="2x" />
                    </LoadingContainer>
                ) : studyPlanError || stepError ? (
                    <ErrorContainer>
                        <ErrorIcon>
                            <FontAwesomeIcon icon={['fas', 'exclamation-circle']} />
                        </ErrorIcon>
                        <ErrorTitle>{t('content.errorTitle')}</ErrorTitle>
                        <ErrorMessage>
                            {(studyPlanError || stepError) instanceof Error
                                ? (studyPlanError || stepError)?.message
                                : t('content.errorMessage')}
                        </ErrorMessage>
                        <RetryButton onClick={() => window.location.reload()}>
                            <FontAwesomeIcon icon={['fas', 'redo']} />
                            {t('content.retryButton')}
                        </RetryButton>
                    </ErrorContainer>
                ) : (
                    <HighlightMenuStyles>
                        <div className="content-wrapper">
                            {stepContent && documentToReactComponents(stepContent.description, options)}
                            
                            {/* Only show highlight menu if user can interact (content sharer or no sharing) */}
                            {canInteract && (
                                <HighlightMenu
                                    target=".content-wrapper"
                                    classNames={{
                                        menu: "mentor-ai-menu",
                                        popover: "mentor-ai-popover",
                                        arrow: "mentor-ai-arrow"
                                    }}
                                    withoutStyles
                                    allowedPlacements={['top', 'bottom']}
                                    offset={0}
                                    menu={({ selectedText, setMenuOpen }) => (
                                        <>
                                            <MenuButton
                                                className="mentor-ai-menu-button"
                                                onClick={() => {
                                                    console.log('Mentor AI Button clicked');
                                                    if (selectedText) {
                                                        handleAskMentorAI(selectedText);
                                                        setMenuOpen(false);
                                                    }
                                                }}
                                            >
                                                <FontAwesomeIcon icon={['far', 'sparkles']} /> {t('askMentorAI', { ns: 'mentor' })}
                                            </MenuButton>
                                        </>
                                    )}
                                />
                            )}
                        </div>
                    </HighlightMenuStyles>
                )}
            </Content>
        </Container>
    )
}

export default StudyPlanContent




// Styled components defined at the top
const Container = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`

const Content = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0 5px;
    color: white;
    scrollbar-width: none;  /* Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    &::-webkit-scrollbar {
        display: none;  /* Chrome, Safari and Opera */
    }
    /* Ensure all content stays within container */
    * {
        max-width: 100%;
    }
    & ::selection {
        background: rgba(77, 0, 119, 0.5);
        color: white;
    }
    
    & ::-moz-selection {
        background: rgba(77, 0, 119, 0.5);
        color: white;
    }
`

// Styles for the highlight menu
const HighlightMenuStyles = styled.div`
    /* Menu container */
    .mentor-ai-menu {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        // padding: 0.5rem;
    }
    
    /* Popover styling */
    .mentor-ai-popover {
        background: linear-gradient(135deg, rgba(41, 0, 102, 0.95) 0%, rgba(77, 0, 119, 0.95) 50%, rgba(0, 86, 136, 0.95) 100%);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 8px;
        color: white;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        z-index: 99999;
    }
    
    /* Arrow styling */
    .mentor-ai-arrow {
        fill: rgba(77, 0, 119, 0.95);
    }
    
    /* Menu button styling */
    .mentor-ai-menu-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1.5rem;
        color: white;
        cursor: pointer;
        font-size: 0.9rem;
        background: transparent;
        border: none;
        text-align: left;
        
        &:hover {
            background: rgba(255, 255, 255, 0.1);
        }
        
        &:active {
            background: rgba(255, 255, 255, 0.2);
        }
    }
`

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: white;
`

const ErrorContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: white;
    text-align: center;
    padding: 2rem;
    gap: 1rem;
`

const ErrorIcon = styled.div`
    font-size: 3rem;
    color: #ff5555;
    margin-bottom: 0.5rem;
`

const ErrorTitle = styled.h3`
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    color: white;
`

const ErrorMessage = styled.p`
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    max-width: 300px;
    line-height: 1.5;
`

const RetryButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 6px;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 0.5rem;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.3);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
    }
`